import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Grid,
    Divider,
    Breadcrumbs,
    Link as MuiLink,
} from "@mui/material";
import {
    ArrowForward as ArrowForwardIcon,
    ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import Layout from "../Util/Layout";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

function formatFileName(fileName) {
    return fileName.replace(/_/g, " ").replace(/\.[^/.]+$/, "");
}

function formatCategoryName(directory) {
    if (directory === "General") return "General";
    return directory.replace(/_/g, " ").replace(/-/g, " ");
}

function generateSlug(fileName) {
    return fileName.replace(/_/g, "-").replace(/\.md$/, "").toLowerCase();
}

// Pull the first H1 as the card title. Falls back to the filename when missing.
function extractTitle(text) {
    const lines = text.split("\n");
    for (const raw of lines) {
        const line = raw.trim();
        if (!line) continue;
        if (line.startsWith("# ")) return line.replace(/^#\s+/, "");
    }
    return null;
}

const WELCOME_FILE = "Welcome_Article.md";

// Directory display order on the index page. Anything not listed falls to the bottom alphabetically.
const SECTION_ORDER = ["OMSCS_Course_Reviews", "ARC-AGI", "General"];

// Hand-written 2-3 line summaries of the full article. Keyed by fullPath (matches require.context output).
const ARTICLE_SUMMARIES = {
    "ARC-AGI/ARC_Problems_-_Overview.md":
        "An introduction to François Chollet's ARC benchmark and why it stays stubbornly hard for modern AI. Covers abstract reasoning, generalization, and few-shot learning. Surveys solution approaches from brute force to DSLs and neural methods.",
    "ARC-AGI/Solving_ARC_Problems_With_A_DSL.md":
        "A walkthrough of using a Domain-Specific Language to solve ARC problems, with Python code for building transformations, generating program strings, and searching for solutions.",
    "OMSCS_Course_Reviews/CS 7637:_Knowledge-Based_Artificial Intelligence_-_Cognitive Systems.md":
        "A review of CS 7637: Knowledge-Based AI.",
    "OMSCS_Course_Reviews/CS 7646:_Machine_Learning_For_Trading.md":
        "A review of CS 7646: Machine Learning for Trading.",
    "OMSCS_Course_Reviews/ISYE 6501:_Intro_To_Analytics_Modeling.md":
        "A review of ISYE 6501: Intro To Analytics Modeling.",
    "OMSCS_Course_Reviews/CS 7650:_Natural_Language_Processing.md":
        "A review of CS 7650: Natural Language Processing.",
};

const ArticlesPage = () => {
    const { articleSlug } = useParams();
    const navigate = useNavigate();
    const [articleStructure, setArticleStructure] = useState({});
    const [articleTitles, setArticleTitles] = useState({}); // fullPath -> title
    const [welcomeContent, setWelcomeContent] = useState("");
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [markdownContent, setMarkdownContent] = useState("");

    useEffect(() => {
        try {
            const markdownContext = require.context(
                "../../Assets/Articles",
                true,
                /\.md$/
            );
            const tempStructure = {};
            markdownContext.keys().forEach((key) => {
                const pathWithoutDot = key.replace("./", "");
                const pathSegments = pathWithoutDot.split("/");
                let directory, fileName;
                if (pathSegments.length === 1) {
                    directory = "General";
                    fileName = pathSegments[0];
                } else {
                    [directory, fileName] = pathSegments;
                }
                if (!tempStructure[directory]) {
                    tempStructure[directory] = [];
                }
                tempStructure[directory].push({
                    name: fileName,
                    fullPath: pathWithoutDot,
                    directory,
                });
            });
            setArticleStructure(tempStructure);
        } catch (error) {
            console.error("Error loading Markdown files:", error);
        }
    }, []);

    // Prefetch each article's text to pull the H1 for card titles and the Welcome body for the intro.
    useEffect(() => {
        const allArticles = Object.values(articleStructure).flat();
        if (allArticles.length === 0) return;

        let cancelled = false;
        (async () => {
            const entries = await Promise.all(
                allArticles.map(async (article) => {
                    try {
                        const module = await import(
                            `../../Assets/Articles/${article.fullPath}`
                        );
                        const response = await fetch(module.default);
                        const text = await response.text();
                        if (article.name === WELCOME_FILE && !cancelled) {
                            setWelcomeContent(text);
                        }
                        return [article.fullPath, extractTitle(text)];
                    } catch (err) {
                        return [article.fullPath, null];
                    }
                })
            );
            if (!cancelled) {
                setArticleTitles(Object.fromEntries(entries));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [articleStructure]);

    useEffect(() => {
        if (articleSlug && Object.keys(articleStructure).length > 0) {
            let found = null;
            Object.values(articleStructure).forEach((articles) => {
                articles.forEach((article) => {
                    if (generateSlug(article.name) === articleSlug) {
                        found = article;
                    }
                });
            });
            if (found) {
                setSelectedArticle(found);
            } else {
                console.warn("Article not found for slug:", articleSlug);
                setSelectedArticle(null);
            }
        } else {
            setSelectedArticle(null);
            setMarkdownContent("");
        }
    }, [articleSlug, articleStructure]);

    const handleArticleClick = (articleObj) => {
        navigate(`/articles/${generateSlug(articleObj.name)}`);
    };

    useEffect(() => {
        if (!selectedArticle) return;
        const loadMarkdown = async () => {
            try {
                const module = await import(
                    `../../Assets/Articles/${selectedArticle.fullPath}`
                );
                const response = await fetch(module.default);
                const text = await response.text();
                setMarkdownContent(text);
            } catch (error) {
                console.error("Error fetching article:", error);
                setMarkdownContent("Failed to load the selected article.");
            }
        };
        loadMarkdown();
    }, [selectedArticle]);

    const indexSections = useMemo(() => {
        const rank = (dir) => {
            const idx = SECTION_ORDER.indexOf(dir);
            return idx === -1 ? SECTION_ORDER.length : idx;
        };
        return Object.entries(articleStructure)
            .map(([directory, articles]) => {
                const visible = articles.filter((a) => a.name !== WELCOME_FILE);
                return [directory, visible];
            })
            .filter(([, visible]) => visible.length > 0)
            .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
    }, [articleStructure]);

    const renderIndex = () => (
        <Box>
            <Box mb={4}>
                <Typography variant="h3" component="h1" gutterBottom>
                    Articles
                </Typography>
                {welcomeContent ? (
                    <Box
                        sx={{
                            color: "text.secondary",
                            "& p": { m: 0, fontSize: "1.05rem", lineHeight: 1.6 },
                            "& h1": { display: "none" },
                        }}
                    >
                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                            {welcomeContent}
                        </ReactMarkdown>
                    </Box>
                ) : (
                    <Typography color="text.secondary">
                        A growing collection of writing on projects, courses, and ideas.
                    </Typography>
                )}
            </Box>

            {indexSections.map(([directory, articles]) => (
                <Box key={directory} mb={5}>
                    <Box display="flex" alignItems="center" mb={2}>
                        <Typography
                            variant="overline"
                            sx={{
                                letterSpacing: 1.2,
                                color: "primary.main",
                                fontWeight: 600,
                            }}
                        >
                            {formatCategoryName(directory)}
                        </Typography>
                        <Divider sx={{ flex: 1, ml: 2 }} />
                    </Box>
                    <Grid container spacing={3}>
                        {articles.map((article) => {
                            const title =
                                articleTitles[article.fullPath] ||
                                formatFileName(article.name);
                            const summary =
                                ARTICLE_SUMMARIES[article.fullPath] ||
                                "Click to read the full article.";
                            return (
                                <Grid item xs={12} sm={6} md={6} key={article.fullPath}>
                                    <Card
                                        elevation={0}
                                        sx={{
                                            height: "100%",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            transition:
                                                "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
                                            "&:hover": {
                                                transform: "translateY(-2px)",
                                                boxShadow: 3,
                                                borderColor: "primary.main",
                                            },
                                        }}
                                    >
                                        <CardActionArea
                                            sx={{ height: "100%", alignItems: "stretch" }}
                                            onClick={() => handleArticleClick(article)}
                                        >
                                            <CardContent
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    height: "100%",
                                                }}
                                            >
                                                <Chip
                                                    label={formatCategoryName(directory)}
                                                    size="small"
                                                    sx={{ alignSelf: "flex-start", mb: 1.5 }}
                                                />
                                                <Typography
                                                    variant="h6"
                                                    component="h2"
                                                    sx={{ fontWeight: 600, mb: 1 }}
                                                >
                                                    {title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 2 }}
                                                >
                                                    {summary}
                                                </Typography>
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    sx={{
                                                        mt: "auto",
                                                        color: "primary.main",
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    <Typography variant="button">
                                                        Read article
                                                    </Typography>
                                                    <ArrowForwardIcon
                                                        fontSize="small"
                                                        sx={{ ml: 0.5 }}
                                                    />
                                                </Box>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            ))}
        </Box>
    );

    const renderArticle = () => (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <MuiLink
                    component="button"
                    underline="hover"
                    onClick={() => navigate("/articles")}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        color: "text.secondary",
                    }}
                >
                    <ArrowBackIcon fontSize="small" sx={{ mr: 0.5 }} />
                    All articles
                </MuiLink>
                {selectedArticle && (
                    <Typography color="text.primary">
                        {formatCategoryName(selectedArticle.directory)}
                    </Typography>
                )}
            </Breadcrumbs>
            {markdownContent ? (
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                    {markdownContent}
                </ReactMarkdown>
            ) : (
                <Typography>Loading article…</Typography>
            )}
        </Box>
    );

    return (
        <Layout>
            <Box p={3}>
                {selectedArticle ? renderArticle() : renderIndex()}
            </Box>
        </Layout>
    );
};

export default ArticlesPage;
